import { useState } from 'react'
import { FileWarning } from 'lucide-react'
import { useRetryInvoicePushes } from '../../hooks/backend/scanPage'
import { INVOICE_PROBLEMS_CHANGED, type InvoiceProblem } from '../../hooks/useHubAlerts'
import { Button } from '../../lib/shadcn/button'
import { toast } from '../../lib/shadcn/sonner'

const SHOWN = 5

// Order lines whose Zoho push failed (not put on the invoice, or not added to
// the Zoho sales order), with Retry and Dismiss. Shown in the Dashboard's
// Today section.
export default function InvoiceProblemsAlert({ problems }: { problems: InvoiceProblem[] }) {
  const retry = useRetryInvoicePushes()
  const [busy, setBusy] = useState<'retry' | 'dismiss' | null>(null)
  if (problems.length === 0) return null

  const run = async (dismiss: boolean) => {
    setBusy(dismiss ? 'dismiss' : 'retry')
    try {
      const res = (await retry.trigger({
        line_ids: problems.map(p => p.line_id),
        dismiss,
      })) as { fixed: number; dismissed: number; failed: { order_id: string; item_name: string; error: string }[] }
      if (dismiss) {
        toast.success(`Cleared ${res.dismissed} from the list`)
      } else if (res.failed.length === 0) {
        toast.success(`All ${res.fixed} pushed to Zoho`)
      } else {
        const first = res.failed[0]!
        toast.error(
          `${res.fixed} pushed, ${res.failed.length} still failing. ${first.order_id} ${first.item_name}: ${first.error}`
        )
      }
    } catch (err) {
      toast.error(`Couldn't ${dismiss ? 'clear' : 'retry'}: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setBusy(null)
      window.dispatchEvent(new Event(INVOICE_PROBLEMS_CHANGED))
    }
  }

  const orders = new Set(problems.map(p => p.order_id)).size
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1.5 py-2">
      <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      <div className="flex-1 min-w-0 text-sm">
        <p>
          <span className="font-medium">
            {problems.length === 1 ? '1 item' : `${problems.length} items`} didn't reach Zoho
          </span>{' '}
          ({orders === 1 ? '1 order' : `${orders} orders`}). Retry, or fix them in Zoho and dismiss.
        </p>
        <ul className="mt-1 space-y-0.5 text-muted-foreground">
          {problems.slice(0, SHOWN).map(p => (
            <li key={p.line_id} className="truncate" title={p.error}>
              {p.order_id}
              {p.customer ? ` · ${p.customer}` : ''} · {p.item_name}:{' '}
              {p.kind === 'order' ? 'not added to the sales order' : 'not on the invoice'} ({p.error})
            </li>
          ))}
          {problems.length > SHOWN && <li>and {problems.length - SHOWN} more</li>}
        </ul>
      </div>
      <div className="flex items-center gap-2 max-sm:w-full max-sm:pl-7">
        <Button size="sm" className="h-8" disabled={busy !== null} onClick={() => run(false)}>
          {busy === 'retry' ? 'Retrying…' : 'Retry'}
        </Button>
        <Button size="sm" variant="ghost" className="h-8" disabled={busy !== null} onClick={() => run(true)}>
          Dismiss
        </Button>
      </div>
    </li>
  )
}
